// React Imports
import React, { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';

// I18n
import { Translate, I18n } from 'react-redux-i18n';

// Lodash
import _ from 'lodash';

// Spectrum Components
import Heading from '@react/react-spectrum/Heading';
import Button from '@react/react-spectrum/Button';
import FieldLabel from '@react/react-spectrum/FieldLabel';
import { Grid, GridRow, GridColumn } from '@react/react-spectrum/Grid';
import Select from '@react/react-spectrum/Select';

// Project Components
import Progress from '../global/Progress';
import { sfLangMapping } from '../../i18n/config';

// Redux Actions
import { selectCompany } from '../../actions/companySearchActions';

// CSS
import '../css/CompanySearch.scss';

/**
 * Component for displaying the Company Select Page.
 *
 * This component displays a dropdown select field that shows matching results for a company.
 *
 * @component
 * @example
 *
 * return (
 *   <CompanySelect />
 * )
 */
class CompanySelect extends Component {
  componentDidMount() {
    document.body.addEventListener('click', this.onApplyJoinClick);
  }

  componentWillUnmount() {
    document.body.removeEventListener('click', this.onApplyJoinClick);
  }

  onApplyJoinClick = (event) => {
    if (event.target.id === 'apply-join') {
      event.preventDefault();
      const { locale } = this.props;
      const sfLocale = Object.keys(sfLangMapping).includes(locale)
        ? sfLangMapping[locale]
        : 'en_US';
      const profile = window.adobeIMS.getUserProfile();
      const guid = profile.userId;
      const enrollLink = `${window.apcConfig.partner.enrollment}?lang=${sfLocale}&fn=${profile.first_name}&ln=${profile.last_name}&em=${profile.email}&aid=${profile.email}&guid=${guid}&countryCode=${profile.countryCode}`;
      window.open(enrollLink, '_self');
    }
  };

  // Renders the currently signed in user's e-mail address for the translation string.
  renderImsEmail = () => window.adobeIMS.getUserProfile().email;

  // Returns the autocomplete suggestions from redux store.
  returnSuggestions = () => {
    const { suggestions } = this.props;
    const spectrumSuggestions = [];
    suggestions.forEach((obj) => {
      spectrumSuggestions.push(
        {
          label: obj.name,
          value: obj.recId,
          data: obj,
        },
      );
    });
    return spectrumSuggestions;
  }

  renderSelectItem = (item) => (
    <div className="autocomplete-itemRow">
      <span className="autocomplete-company-name">{item.label}</span>
      <br />
      <span className="autocomplete-country">{item.data.country}</span>
    </div>
  );

  // Handle the clicking of the Start Enrollment button
  handleSubmit = () => {
    const { history, selectedCompany } = this.props;
    if (selectedCompany) {
      history.push('/personal_information');
    }
  };

  // Handle the selection of a dropdown item
  handleSelectItem = (id) => {
    const { selectCompanyAction, suggestions } = this.props;
    const companyObj = _.find(suggestions, (company) => company.recId === id);
    // Get the object with the correct value:
    selectCompanyAction(companyObj);
  };

  // Decides whether we can enable the submit button.
  renderSubmitButton = () => {
    const { selectedCompany } = this.props;
    if (selectedCompany) {
      return false;
    }
    return true;
  };

  suggestionLength = () => {
    const { suggestions } = this.props;
    return suggestions.length;
  }

  render() {
    return (
      <div className="page-companySelect">
        <Progress step={1} />
        <div className="page-container">
          <Heading>
            <Translate data-testid="find-company" value="workflow.find_company.header" />
          </Heading>
          <p>
            <Translate data-testid="company-matches" value="workflow.find_company.company_matches" count={this.suggestionLength()} email={this.renderImsEmail()} />
          </p>
          <Grid variant="fixed" className="field-grid">
            <GridRow>
              <GridColumn
                size={[12, 6]}
              >
                <div className="company-select">
                  <FieldLabel
                    label={I18n.t('workflow.find_company.company_name')}
                    necessityIndicator="icon"
                  >
                    <Select
                      data-testid="select-company"
                      closeOnSelect
                      renderItem={(item) => this.renderSelectItem(item)}
                      onChange={(item) => this.handleSelectItem(item)}
                      placeholder={I18n.t('workflow.find_company.select_company')}
                      multiple={false}
                      options={this.returnSuggestions()}
                    />
                  </FieldLabel>
                </div>
              </GridColumn>
            </GridRow>
          </Grid>
          <div className="button-container">
            <Button data-testid="submit-button" variant="cta" disabled={this.renderSubmitButton()} onClick={this.handleSubmit}><Translate value="workflow.find_company.start_enrollment" /></Button>
          </div>
          <p>
            <Translate data-testid="company-not-listed" dangerousHTML value="workflow.find_company.company_not_listed" />
          </p>
        </div>
      </div>
    );
  }
}
CompanySelect.propTypes = {
  /**
   * autocompleteSuggestions {Array} An array of objects, provided by mapStateToProps.
   */
  suggestions: PropTypes.arrayOf(PropTypes.object),
  /**
   * selectedCompany {Object} The currently selected company, provided by mapStateToProps.
   * TODO: Update with the final object shape once API is finished.
   */
  selectedCompany: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    country: PropTypes.string,
  }),
  /**
   * selectCompanyAction {Function} Redux action to set the selected company. Provided by
   * mapDispatchToProps.
   */
  selectCompanyAction: PropTypes.func.isRequired,
  /**
   * history {Object} The history object provided by React Router. Used to navigate to other pages.
   */
  history: PropTypes.shape({
    history: PropTypes.object,
    location: PropTypes.object,
    match: PropTypes.object,
    push: PropTypes.func,
  }).isRequired,
  /**
   * locale {String} The currently selected locale.
   */
  locale: PropTypes.string.isRequired,
};
CompanySelect.defaultProps = {
  suggestions: [],
  selectedCompany: null,
};

const mapStateToProps = (state) => ({
  selectedCompany: state.company.selectedCompany,
  suggestions: state.company.suggestions,
  locale: state.i18n.locale,
});

const mapDispatchToProps = {
  selectCompanyAction: selectCompany,
};

export default connect(mapStateToProps, mapDispatchToProps)(CompanySelect);